import React from 'react';

import IShopInfo from './interfaces/IShopInfo';
import UCWindow from './interfaces/UCWindow';
import { DatabaseContext } from '../database/DatabaseHandler';
import { ParametersContext } from '../parametersProcessor/ParametersProcessor';
import { CartContext } from '../cart/CartHandler';

import UnityConfig from './types/UnityConfig';
import UnityProgressBar from './UnityProgressBar';
import UnityWarningLayer from './UnityWarningLayer';
import ICabinetResult from './interfaces/ICabinetResult';
import UnityHandler from '../../designer/UnityHandler';
import PrestaGlobals from '../common/PrestaGlobals';
import { VIEW_OPTIONS, readViewOption } from './viewOptions';
import VisibilityMenu from './VisibilityMenu';
import {
  ALL_VISIBLE, FRONTS_ROW, VisibilityMap, VisibilityRow, parseVisibility, rowState, visibilityRequest, withRow
} from './visibilityLayers';
import IUnityTransport from './transport/IUnityTransport';
import { isUnityEditorModeEnabled } from './transport/unityEditorMode';
import UnityEditorTransport, { IUnityEditorState } from './transport/UnityEditorTransport';
import UnityEditorStatusPanel from './transport/UnityEditorStatusPanel';

import './UnityView.css';

export const ICONPATH: string = "/modules/ubercabinet/views/images/designer-icons/";

/** Declaration for the createUnityInstance function */
declare function createUnityInstance(canvas: HTMLElement, config: UnityConfig, onProgress: (progress: number) => void): Promise<any>;

/** Declaration for the UnityInstance class */
declare class UnityInstance {
  public SendMessage(gameObjectName: string, methodName: string, message?: string): void;
  public Quit(): Promise<void>;
}

type ToolbarOption = {
  id: string;
  label: string;
  asciiIcon?: string;
  svgSrc?: string;
  textColor?: string;
  params: [string, string];
};

type ToolbarColumn = {
  id: string;
  type: 'button' | 'dropdown';
  /** Show the active option's label as the button's tooltip. */
  tooltip?: boolean;
  defaultOptionId: string;
  options: ToolbarOption[];
};


const unityOverlayToolbar: ToolbarColumn[] = [
  {
    id: 'SetRotating',
    type: 'button',
    defaultOptionId: 'True',
    options: [
      { id: 'True', label: 'Auto mode', svgSrc: ICONPATH+'auto-rotate.svg', params: ['SetRotating', 'true'] },
      { id: 'False', label: 'Manual mode', svgSrc: ICONPATH+'manual-rotate.svg', params: ['SetRotating', 'false'] }
    ]
  },
  {
    // a toggle: the icon shows the doors as they are now, a click turns them the other way
    id: 'SetDoorsOpen',
    type: 'button',
    tooltip: true,
    defaultOptionId: 'doTheClose',
    options: [
      { id: 'doTheClose', label: 'Ajtók kinyitása', svgSrc: ICONPATH+'door-closed.svg', params: ['SetDoorsOpen', 'false'] },
      { id: 'doTheOpen', label: 'Ajtók becsukása', svgSrc: ICONPATH+'door-open.svg', params: ['SetDoorsOpen', 'true'] }
    ]
  },
  {
    // the lighting preset the room is lit in: a click lights it in the next one (Day, Spots, Evening).
    // A view setting - the design keeps its own mood; Unity answers with the "Lighting" view change
    id: 'SetLighting',
    type: 'button',
    tooltip: true,
    defaultOptionId: 'Day',
    options: [
      { id: 'Day', label: 'Nappali fény', svgSrc: ICONPATH+'light-day.svg', params: ['SetLighting', 'Day'] },
      { id: 'Spots', label: 'Spotlámpák', svgSrc: ICONPATH+'light-spots.svg', params: ['SetLighting', 'Spots'] },
      { id: 'Evening', label: 'Esti fény', svgSrc: ICONPATH+'light-evening.svg', params: ['SetLighting', 'Evening'] }
    ]
  },
  {
    id: 'SetExploded',
    type: 'button',
    defaultOptionId: 'normal',
    options: [
      { id: 'normal', label: 'Normal', svgSrc: ICONPATH+'normal.svg', params: ['SetExploded', 'false'] },
      { id: 'exploded', label: 'Exploded', svgSrc: ICONPATH+'exploded.svg', params: ['SetExploded', 'true'] }
    ]
  },
  {
    id: 'SetViewMode',
    type: 'button',
    defaultOptionId: 'Free',
    options: [
      { id: 'Free', label: 'View mode Free', svgSrc: ICONPATH+'view-mode-free.svg', params: ['SetViewMode', 'Free'] },
      { id: 'FitToScreen', label: 'View mode FitToScreen', svgSrc: ICONPATH+'view-mode-fit.svg', params: ['SetViewMode', 'FitToScreen'] },
      { id: 'Front', label: 'View mode Front', svgSrc: ICONPATH+'view-mode-front.svg', params: ['SetViewMode', 'Front'] },
      { id: 'Side', label: 'View mode Side', svgSrc: ICONPATH+'view-mode-side.svg', params: ['SetViewMode', 'Side'] },
      { id: 'Top', label: 'View mode Top', svgSrc: ICONPATH+'view-mode-top.svg', params: ['SetViewMode', 'Top'] }
    ]
  },
  {
    id: 'SetShadingMode',
    type: 'dropdown',
    defaultOptionId: 'Shaded',
    options: [
      { id: 'Shaded', label: 'Solid cube', svgSrc: ICONPATH+'shaded.svg', params: ['SetShadingMode', 'Shaded'] },
      { id: 'Wireframe', label: 'Grid cube', svgSrc: ICONPATH+'wireframe.svg', params: ['SetShadingMode', 'Wireframe'] },
      { id: 'ShadedWireframe', label: 'Outline cube', svgSrc: ICONPATH+'shaded-wireframe.svg', params: ['SetShadingMode', 'ShadedWireframe'] }
    ]
  }
];

const getInitialToolbarState = () => unityOverlayToolbar.reduce<Record<string, string>>((state, column) => {
  state[column.id] = column.defaultOptionId;
  return state;
}, {});

const getToolbarOption = (column: ToolbarColumn, optionId: string) => {
  return column.options.find((option) => option.id === optionId) ?? column.options[0];
};

const renderToolbarIcon = (option: ToolbarOption) => {
  if (option.svgSrc) {
    return <img className="unity-overlay-toolbar__icon" src={option.svgSrc} alt="" aria-hidden="true" />;
  }

  return option.asciiIcon;
};


/** Main UnityView component to load and display the Unity WebGL content */
interface UnityProps {
  shopInfo: IShopInfo;
  UCOnLoadedReceived: () => void;
  UCOrderReceived: (result: any) => void;
  UCViewChangedReceived: (key: string, value: string) => void;
}

const UnityView: React.FC<UnityProps> = ({ shopInfo, UCOnLoadedReceived, UCOrderReceived, UCViewChangedReceived }) => {

  // Use the DatabaseContext to access global data
  const dataContext = React.useContext(DatabaseContext);
  const cartContext = React.useContext(CartContext);

  // Use the ParametersContext to access parameters
  const { parameters, generateStyleScript } = React.useContext(ParametersContext);

  const buildUrl = React.useRef("/Build");
  const loaderUrl = React.useRef(`${buildUrl.current}/Build.loader.js`);
  const dataUrl = React.useRef(`${buildUrl.current}/Build.data.gz`);
  const frameworkUrl = React.useRef(`${buildUrl.current}/Build.framework.js.gz`);
  const codeUrl = React.useRef(`${buildUrl.current}/Build.wasm.gz`);

  // Use a ref to persist config object so showBanner can be set by UnityWarning
  const configRef = React.useRef<UnityConfig>({
    dataUrl: dataUrl.current,
    frameworkUrl: frameworkUrl.current,
    codeUrl: codeUrl.current,
    streamingAssetsUrl: "StreamingAssets",
    companyName: "MagTech",
    productName: "UberDesigner",
    productVersion: "0.1.0",
    showBanner: null, // Will be set by UnityWarning
    matchWebGLToCanvasSize: true,
    devicePixelRatio: 1,
    // The AI designer page: the AI designer reads the canvas back for screenshots on a Unity build
    // that predates WebInterface.Screenshot, which needs the drawing buffer kept after each frame.
    ...(PrestaGlobals.isAiDesigner ? { webglContextAttributes: { preserveDrawingBuffer: true } } : {})
  });
  const config = configRef.current;

  // A transport típusa `IUnityTransport`: a `SendMessage` szignatúrája azonos a WebGL instance-éval,
  // ezért az alábbi hívási helyek egyike sem tud róla, melyik van a másik végen.
  const [instance, setInstance] = React.useState<IUnityTransport | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [progress, setProgress] = React.useState(0);
  const [loadedReceived, setLoadedReceived] = React.useState<boolean>(false);
  const [toolbarState, setToolbarState] = React.useState<Record<string, string>>(getInitialToolbarState);
  const [openToolbarColumnId, setOpenToolbarColumnId] = React.useState<string | null>(null);
  // Unity reports the "None" lighting when the scene has no room: then there is nothing to light
  const [hasRoom, setHasRoom] = React.useState<boolean>(true);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  // "Unity editor mode": MOUNT-IDEJŰ döntés, a mount alatt nem változhat (a váltás újratöltéssel jár —
  // lásd transport/unityEditorMode.ts). Ezért ref: egyetlen igazságforrás arra, hogy ez a mount melyik
  // úton megy, és a render sem tud félúton átbillenni. Csak ADMIN módban él: fejlesztői eszköz.
  const editorMode = React.useRef<boolean>(PrestaGlobals.isAdmin && isUnityEditorModeEnabled());
  const [editorState, setEditorState] = React.useState<IUnityEditorState>(
    { status: 'connecting', generation: 0, lastError: null }
  );
  const pendingPriceCalculation = React.useRef<boolean>(false);

  // The layers shown in the viewer ("Rétegek" menu). The Unity "Visibility" view change is the source
  // of truth; the ref lets the toolbar callbacks below read the latest map without re-creating them.
  const [visibility, setVisibility] = React.useState<VisibilityMap>(ALL_VISIBLE);
  const visibilityRef = React.useRef<VisibilityMap>(ALL_VISIBLE);

  const applyVisibility = React.useCallback((next: VisibilityMap) => {
    visibilityRef.current = next;
    setVisibility(next);
    instance?.SendMessage('WebInterface', 'SetVisibility', visibilityRequest(next));
  }, [instance]);

  const sendStateToUnity = React.useCallback((columnId: string, optionId: string) => {
    if (instance) {
      // For the open/close door action, show the fronts first, then open/close the doors.
      if((optionId=="doTheOpen" || optionId=="doTheClose") && rowState(FRONTS_ROW, visibilityRef.current) !== 'shown') {
        applyVisibility(withRow(visibilityRef.current, FRONTS_ROW, true));
      }
      let col = unityOverlayToolbar.find(obj => obj.id === columnId);
      if(col) {
        let action = col.options.find(obj => obj.id === optionId);
        if(action) {
          instance.SendMessage('WebInterface', action.params[0], action.params[1]);
        }
      }
    }

  }, [instance, applyVisibility]);

  const handleVisibilityRowChange = React.useCallback((row: VisibilityRow, shown: boolean) => {
    applyVisibility(withRow(visibilityRef.current, row, shown));
  }, [applyVisibility]);

  const handleShowAllLayers = React.useCallback(() => {
    applyVisibility(ALL_VISIBLE);
  }, [applyVisibility]);

  const handleToolbarChange = React.useCallback((columnId: string, optionId: string) => {

    setOpenToolbarColumnId(null);

    setToolbarState((currentState) => ({
      ...currentState,
      [columnId]: optionId
    }));


  }, [instance]);

  const handleSingleButtonClick = React.useCallback((column: ToolbarColumn) => {

    setOpenToolbarColumnId(null);

    setToolbarState((currentState) => {
      const currentOptionId = currentState[column.id] ?? column.defaultOptionId;
      const currentOptionIndex = column.options.findIndex((option) => option.id === currentOptionId);
      const nextOption = column.options[(currentOptionIndex + 1) % column.options.length] ?? column.options[0];

      sendStateToUnity(column.id, nextOption.id);

      return {
        ...currentState,
        [column.id]: nextOption.id
      };
    });

  }, [instance]);

  const handleDropdownTriggerClick = React.useCallback((columnId: string) => {
    setOpenToolbarColumnId((currentColumnId) => currentColumnId === columnId ? null : columnId);
  }, []);

  const handleDropdownOptionClick = React.useCallback((columnId: string, optionId: string) => {
    sendStateToUnity(columnId, optionId);
    handleToolbarChange(columnId, optionId);
    setOpenToolbarColumnId(null);
  }, [handleToolbarChange]);

  // BRIDGE MODE: No loader, No wasm-loading, even no canvas — The 3D inside the Editor Game view
  // látszik. Külön effekt a WebGL mount-effekt ELŐTT: a kettő közül mindig pontosan az egyik tesz
  // bármit, mert az `editorMode` ref mount-időben eldől és nem változik.
  React.useEffect(() => {
    if (!editorMode.current) return;

    const transport = new UnityEditorTransport(setEditorState);
    setInstance(transport);
    UnityHandler.instance = transport;
    setLoading(false);   // nincs mit letölteni

    return () => {
      transport.dispose();
      UnityHandler.instance = null;
    };
  }, []);

  React.useEffect(() => {
    if (editorMode.current) return;

    // Set meta info according to device type
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      let meta: HTMLMetaElement = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, height=device-height, initial-scale=1.0, user-scalable=no, shrink-to-fit=yes';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Unmounted before/after load -> quit, don't publish the instance
    let cancelled = false;
    let created: UnityInstance | null = null;

    // Dynamically load Unity loader script
    const script = document.createElement('script');
    script.src = loaderUrl.current;

    script.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;
      createUnityInstance(canvas, config, (progress: number) => {
        if (!cancelled) setProgress(progress);
      })
        .then((unityInstance: UnityInstance) => {
          if (cancelled) {
            unityInstance?.Quit().catch(() => {});
            return;
          }
          created = unityInstance;
          setInstance(unityInstance);
          UnityHandler.instance = unityInstance;
          setLoading(false);
          
          if (unityInstance == null) {
            console.error("Unity instance is not ready after loading!");
            return;
          }

          console.log("[Unity::onload] Unity instance created successfully!");
        })
        .catch((message: string) => {
          if (!cancelled && config.showBanner) config.showBanner(message, 'error');
        });
    };
    document.body.appendChild(script);

    // Show loading bar on mount
    setLoading(true);
    setProgress(0);

    // Clean up script on unmount
    return () => {
      cancelled = true;
      document.body.removeChild(script);
      if (created) {
        if (UnityHandler.instance === created) UnityHandler.instance = null;
        created.Quit().catch(() => {});
        created = null;
      } else {
        UnityHandler.instance = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaderUrl.current]);

  // If instance is ready, set the shop info
  React.useEffect(() => {
    if (instance) {
      console.log("[Unity::instanceReady] Setting shop...");
      instance.SendMessage('WebInterface', 'SetShop', JSON.stringify(shopInfo));
    }
  }, [instance, editorState.generation]);

  // If instance is ready and uconScript is provided, set the script
  React.useEffect(() => {
    if (instance && loadedReceived && dataContext.uconScript) {
      console.log("[Unity::uconScript] Sending ucon script to Unity...");
      instance.SendMessage('WebInterface', 'SetUConScript', JSON.stringify(dataContext.uconScript));
      console.log("SetUConScript: ", JSON.stringify(dataContext.uconScript, null, 2))
    }
  }, [instance, loadedReceived, dataContext.uconScript, editorState.generation]);

  // If instance is ready and style script are provided, set the style script
  React.useEffect(() => {
    if (instance && loadedReceived && parameters && Object.keys(parameters).length > 0) {
      console.log("[Unity::parameters] Sending style script to Unity...");
      // TODO: Workaround what may be not the best solution.
      // cartContext.setCabinetResult(null); // Clear previous cabinet result when parameters change
      // A Unity-nak küldött stylescriptet a ParametersProcessor generálja (Decor él-normalizálás +
      // BackPassing → CabinetFrame Relations). A view csak elküldi — nincs benne param-logika.
      // Admin override (StyleScriptTab / reopened script design) wins over the parameters
      const paramsArray = cartContext.designStyle ?? generateStyleScript();
      // A TÉNYLEGES Unity-kimenet (build-tier rendezett + Relations-merge-elt) — NEM azonos a
      // generateParameterScripts kosár-loggal, ami a paramonkénti (sort_index) sorrendet mutatja.
      console.log("[Unity::parameters] SetStyleScript:", JSON.stringify(paramsArray, null, 2));
      instance.SendMessage('WebInterface', 'SetStyleScript', JSON.stringify(paramsArray));
      // Mark that we need to calculate price when cabinetResult updates
      pendingPriceCalculation.current = true;
      // Jelezzük a CartHandlernek: a cabinetResult mostantól ELAVULT, amíg a Unity nem
      // válaszol (UCOrderReceived). A Kosárba gomb erre vár, ha közben nyomták meg —
      // így a blur-rel frissített paraméter (pl. méret) már az ÚJ értékkel kerül kosárba.
      cartContext.notifyStyleScriptSent();
    }
  }, [instance, loadedReceived, parameters, cartContext.designStyle, editorState.generation]);

  // Calculate price when cabinetResult updates after new parameters are sent
  React.useEffect(() => {
    if (pendingPriceCalculation.current && cartContext.cabinetResult) {
      console.log("[Unity::cabinetResult] Cabinet result updated, calculating price with latest parameters...");
      cartContext.getQuickAndDirtyPrice();
      pendingPriceCalculation.current = false;
    }
  }, [cartContext.cabinetResult]);

  // Register UCOnLoadedReceived handler on mount
  React.useEffect(() => {
      (window as UCWindow).UCOnLoadedReceived = function() {
        console.log("[Unity::UCOnLoadedReceived] UCOnLoadedReceived called");
        setLoadedReceived(true);
        UCOnLoadedReceived();
      };
      return () => {
          // Clean up handler on unmount
          // @ts-expect-error: allow removing handler
          delete (window as UCWindow).UCOnLoadedReceived;
      };
  }, [instance, UCOnLoadedReceived]);

  // Register UCOrderReceived handler on mount
  React.useEffect(() => {
      (window as UCWindow).UCOrderReceived = function(result: string) {
          console.log("[Unity::UCOrderReceived] UCOrderReceived called");
          try {
            const parsedResult: ICabinetResult = JSON.parse(result);
            UCOrderReceived(parsedResult);
            cartContext.setCabinetResult(parsedResult);
          } catch (e) {
            console.error("Failed to parse cabinet result JSON:", e);
            cartContext.setCabinetResult(null);
            return;
          }
      };
      return () => {
          // Clean up handler on unmount
          // @ts-expect-error: allow removing handler
          delete (window as UCWindow).UCOrderReceived;
      };
  }, [instance, UCOrderReceived]);

  // Register UCViewChangedReceived handler on mount
  React.useEffect(() => {
      (window as UCWindow).UCViewChangedReceived = function(key: string, value: string) {
        console.log("[Unity::UCViewChangedReceived] UCViewChangedReceived called with key:", key, "value:", value);

        if(key=="Rotating") {
          handleToolbarChange("SetRotating", value);
          handleToolbarChange("SetViewMode", "Free");
        }
        else if(key=="CurrentViewMode") {
          handleToolbarChange("SetViewMode", value);
        }
        else if(key=="OpenDoors") {
          handleToolbarChange("SetDoorsOpen", value=="True" ? "doTheOpen" : "doTheClose");
        }
        else if(key=="Lighting") {
          // the mood the room is lit in, also after every build; "None" means no room - the button hides,
          // "Studio" leaves it as it is
          setHasRoom(value!="None");
          if (value=="Day" || value=="Spots" || value=="Evening") {
            handleToolbarChange("SetLighting", value);
          }
        }
        else if(key=="Visibility") {
          const map = parseVisibility(value);
          if (map) {
            visibilityRef.current = map;
            setVisibility(map);
          }
        }

        UCViewChangedReceived(key, value);
      };
      return () => {
          // Clean up handler on unmount
          // @ts-expect-error: allow removing handler
          delete (window as UCWindow).UCViewChangedReceived;
      };
  }, [instance, UCViewChangedReceived]);

  // Register UCSelectedToBrowserReceived handler on mount. Unity calls this when the user
  // selects/clicks an element in the viewer (e.g. with overlay highlight on). We don't act
  // on it yet — just log the parameters so the callback exists (prevents "is not a function").
  React.useEffect(() => {
      (window as UCWindow).UCSelectedToBrowserReceived = function(...args: any[]) {
        console.log("[Unity::UCSelectedToBrowserReceived] called with:", ...args);
      };
      return () => {
          // Clean up handler on unmount
          // @ts-expect-error: allow removing handler
          delete (window as UCWindow).UCSelectedToBrowserReceived;
      };
  }, [instance]);

  // Register UCDimensionLabelsReceived handler on mount. Unity calls this (e.g. with the
  // dimension gizmo on) to push dimension labels to the browser. Not acted on yet — just
  // log the parameters so the callback exists (prevents "is not a function").
  React.useEffect(() => {
      (window as UCWindow).UCDimensionLabelsReceived = function(...args: any[]) {
        console.log("[Unity::UCDimensionLabelsReceived] called with:", ...args);
      };
      return () => {
          // Clean up handler on unmount
          // @ts-expect-error: allow removing handler
          delete (window as UCWindow).UCDimensionLabelsReceived;
      };
  }, [instance]);

  // Register UCDimensionPositionsReceived handler on mount. Unity pushes dimension-label
  // positions to the browser (dimension gizmo). Not acted on yet — just log the parameters
  // so the callback exists (prevents "is not a function").
  React.useEffect(() => {
      (window as UCWindow).UCDimensionPositionsReceived = function(...args: any[]) {
        console.log("[Unity::UCDimensionPositionsReceived] called with:", ...args);
      };
      return () => {
          // Clean up handler on unmount
          // @ts-expect-error: allow removing handler
          delete (window as UCWindow).UCDimensionPositionsReceived;
      };
  }, [instance]);

  // Re-apply the admin "Nézet" view toggles (overlay highlight / dimension gizmo) once the
  // model has loaded, so they survive a page reload without the admin re-flipping them. State
  // lives in sessionStorage (viewOptions.ts) → cleared on browser restart. Admin-only, and the
  // keys are only ever written by the admin Nézet tab, so this is a no-op for customers.
  //
  // IMPORTANT: this effect MUST stay declared AFTER all the UC*Received registration effects
  // above. Those callback props aren't memoized, so their effects re-run every commit; React
  // flushes all cleanups (which delete the window.UC* handlers) before any creates. Sending a
  // gizmo command makes Unity synchronously call back into window.UCViewChangedReceived — so we
  // must send only after the registration creates (which re-add the handlers) have run, i.e.
  // from the last-declared effect. Moving this earlier reintroduces a "UC*Received is not a
  // function" crash on reload.
  React.useEffect(() => {
    if (instance && loadedReceived && PrestaGlobals.isAdmin) {
      VIEW_OPTIONS.forEach((opt) => {
        if (readViewOption(opt.method)) {
          instance.SendMessage('WebInterface', opt.method, 'true');
        }
      });
    }
  }, [instance, loadedReceived, editorState.generation]);

  return (
    <div className="unity-designer" style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
      <div id="unity-container-overlay">
        <div className="unity-overlay-toolbar" aria-label="Unity view toolbar">
          {unityOverlayToolbar.filter((column) => hasRoom || column.id !== 'SetLighting').map((column) => {
            const activeOption = getToolbarOption(column, toolbarState[column.id]);
            const isDropdown = column.type === 'dropdown';
            const isOpen = openToolbarColumnId === column.id;

            return (
              <div
                key={column.id}
                className={[
                  'unity-overlay-toolbar__column',
                  isDropdown ? 'unity-overlay-toolbar__column--dropdown' : '',
                  isOpen ? 'unity-overlay-toolbar__column--open' : ''
                ].filter(Boolean).join(' ')}
              >
                <button
                  type="button"
                  className={[
                    'unity-overlay-toolbar__button',
                    'unity-overlay-toolbar__button--active',
                    isDropdown ? 'unity-overlay-toolbar__button--dropdown' : ''
                  ].filter(Boolean).join(' ')}
                  style={{ color: activeOption.textColor }}
                  aria-label={activeOption.label}
                  title={column.tooltip ? activeOption.label : undefined}
                  aria-haspopup={isDropdown ? 'menu' : undefined}
                  aria-expanded={isDropdown ? isOpen : undefined}
                  onClick={() => isDropdown ? handleDropdownTriggerClick(column.id) : handleSingleButtonClick(column)}
                >
                  {renderToolbarIcon(activeOption)}
                </button>

                {isDropdown && (
                  <div className="unity-overlay-toolbar__menu" role="menu">
                    {column.options
                      .filter((option) => option.id !== activeOption.id)
                      .map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className="unity-overlay-toolbar__button"
                          style={{ color: option.textColor }}
                          role="menuitem"
                          aria-label={option.label}
                          onClick={() => handleDropdownOptionClick(column.id, option.id)}
                        >
                          {renderToolbarIcon(option)}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            );
          })}

          <VisibilityMenu
            iconSrc={ICONPATH + 'layers.svg'}
            open={openToolbarColumnId === 'SetVisibility'}
            visibility={visibility}
            onToggleOpen={() => handleDropdownTriggerClick('SetVisibility')}
            onRowChange={handleVisibilityRowChange}
            onShowAll={handleShowAllLayers}
          />
        </div>
      </div>

      <div id="unity-container" style={{ width: '100%', height: '100%', position: 'absolute' }}>
        {editorMode.current ? (
          <UnityEditorStatusPanel state={editorState} />
        ) : (
          <>
            <canvas id="unity-canvas" ref={canvasRef} tabIndex={-1} style={{ width: '100%', height: '100%' }} />
            <UnityProgressBar loading={loading} progress={progress} />
          </>
        )}
      </div>
    </div>
  );
}

export default UnityView;
